/* storybook-check-ignore */
import { FC, useState } from 'react';

import { CONSUMER_URL } from 'components/globals';
import { useTrackProperty } from 'components/marketplace/helpers';
import {
  determinePropertyActionability,
  TOUR_ACTION_TEXT,
} from 'components/marketplace/helpers/propertyActionability';

import { useObservability } from 'helpers/observability';

import { FloatingCTA } from './PropertySection/FloatingCTA';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';
import { PropertyDetailsType } from './types';

interface FooterCTAProps {
  property: PropertyDetailsType;
  isVisible?: boolean;
}

export const FooterCTA: FC<FooterCTAProps> = ({ property, isVisible = true }) => {
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { isActionable, tourKind, showBuyDirect, disableBuyDirect } =
    determinePropertyActionability(property);

  // Only show FooterCTA for actionable properties
  if (!isActionable || !tourKind) {
    return null;
  }

  const handleSelfTourClick = () => {
    trackProperty('property-self-tour', 'marketplace-pdp-footer-cta');
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement: 'footer',
    });
  };

  const handleCtaClick = () => {
    if (showBuyDirect) {
      trackProperty('property-buy-direct', 'marketplace-pdp-footer-cta');
      trackJourneyEvent('marketplaceProperty', 'offerStart', {
        addressUuid: property.addressToken,
        source: 'footer',
      });
    } else if (tourKind === 'selfTour') {
      handleSelfTourClick();
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const getCtaText = () => {
    if (showBuyDirect) {
      return disableBuyDirect ? 'Offer pending' : 'Buy this home';
    }
    return TOUR_ACTION_TEXT[tourKind];
  };

  const getCtaHref = () => {
    if (showBuyDirect) {
      return `${CONSUMER_URL}/dashboard/buy/checkout?id=${property.addressToken}`;
    }
    if (tourKind === 'selfTour') {
      return `${CONSUMER_URL}/dashboard/buy/tour?id=${property.addressToken}`;
    }
    return undefined;
  };

  const getVariant = () => {
    if (showBuyDirect) {
      return 'primary';
    }
    return tourKind === 'tourWithAgent' ? 'secondary' : 'primary';
  };

  return (
    <>
      <FloatingCTA
        ctaText={getCtaText()}
        onClickCta={handleCtaClick}
        ctaHref={getCtaHref()}
        isLiked={property.isLiked}
        onClickReaction={property.onClickReaction}
        variant={getVariant()}
        isVisible={isVisible}
        disabled={showBuyDirect && disableBuyDirect}
        showSecondaryButton={showBuyDirect}
        secondaryButtonText={showBuyDirect ? 'Self-tour' : undefined}
        secondaryButtonHref={
          showBuyDirect
            ? `${CONSUMER_URL}/dashboard/buy/tour?id=${property.addressToken}`
            : undefined
        }
        onClickSecondary={showBuyDirect ? handleSelfTourClick : undefined}
      />
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={handleClose} />
      )}
    </>
  );
};
